import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Star,
  ArrowLeft,
  ShoppingCart,
  Package,
  Minus,
  Plus,
  Tag,
  Sparkles,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
} from 'lucide-react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { fetchProductById, selectSelectedDress, selectDressesLoading } from '../store/slices/dressesSlice';
import { addToCart, openCart } from '../store/slices/cartSlice';
import { showToast } from '../store/slices/toastSlice';
import { pushViewItem, pushAddToCart } from '../utils/gtm';
import LoadingSpinner from '../components/LoadingSpinner';

const InfoSection = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-gray-100">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center justify-between w-full py-3.5 text-sm font-semibold text-gray-900 hover:text-primary-600 transition-colors"
      >
        {title}
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="pb-4 text-sm text-gray-600 leading-relaxed">{children}</div>}
    </div>
  );
};

const ProductPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const product = useSelector(selectSelectedDress);
  const loading = useSelector(selectDressesLoading);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addedFeedback, setAddedFeedback] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    dispatch(fetchProductById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (product) {
      const imgs = product.images?.length > 0 ? product.images : (product.image ? [{ id: 0, url: product.image, altText: product.name }] : []);
      const primaryIdx = imgs.findIndex(img => img.url === product.image);
      setSelectedIndex(primaryIdx >= 0 ? primaryIdx : 0);
      const firstInStock = product.variants?.find(v => v.stockQuantity > 0);
      setSelectedVariant(firstInStock || product.variants?.[0] || null);
      setQuantity(1);
    }
  }, [product]);

  useEffect(() => {
    if (product) {
      pushViewItem(product, product.price);
    }
  }, [product]);

  const handleAddToCart = () => {
    if (!product) return;
    const size = selectedVariant?.size || 'Default';
    const price = selectedVariant?.finalPrice || product.price;
    dispatch(addToCart({
      dress: { ...product, price },
      size,
      variantId: selectedVariant?.id || null,
      quantity,
    }));
    dispatch(openCart());
    pushAddToCart({
      id: product.id,
      name: product.name,
      price,
      category: product.category || '',
      subCategory: product.subCategory || '',
      originalPrice: product.originalPrice || null,
      size,
    }, quantity);
    setAddedFeedback(true);
    setTimeout(() => setAddedFeedback(false), 2000);
  };

  const canAdd = product?.inStock && (!product.variants?.length || (selectedVariant && selectedVariant.stockQuantity > 0));
  const maxQty = selectedVariant?.stockQuantity || 999;

  const handleIncreaseQuantity = () => {
    if (quantity >= maxQty) {
      dispatch(showToast({
        message: `You've selected the maximum number of products available in this size (${maxQty}).`,
        type: 'error',
      }));
      return;
    }
    setQuantity(q => Math.min(maxQty, q + 1));
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Product not found</h2>
        <p className="text-gray-500 mb-6">This product may no longer be available.</p>
        <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-full text-sm font-medium hover:bg-primary-700 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Store
        </Link>
      </div>
    );
  }

  const discount = product.discountPercentage ||
    (product.originalPrice && product.originalPrice > product.price
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : 0);

  const displayPrice = selectedVariant?.finalPrice || product.price;
  const displayImages = product.images?.length > 0 ? product.images : (product.image ? [{ id: 0, url: product.image, altText: product.name }] : []);
  const selectedImage = displayImages[selectedIndex]?.url || null;

  const goPrev = () => setSelectedIndex(i => (i - 1 + displayImages.length) % displayImages.length);
  const goNext = () => setSelectedIndex(i => (i + 1) % displayImages.length);

  const onImageTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onImageTouchEnd = (e) => {
    if (touchStartX.current == null || displayImages.length <= 1) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      if (delta > 0) goPrev(); else goNext();
    }
    touchStartX.current = null;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-primary-600 transition-colors">Store</Link>
        <span>/</span>
        {product.category && (
          <>
            {product.categoryId ? (
              <Link to={`/category/${product.categoryId}`} className="text-gray-400 hover:text-primary-600 transition-colors">
                {product.category}
              </Link>
            ) : (
              <span className="text-gray-400">{product.category}</span>
            )}
            <span>/</span>
          </>
        )}
        {product.subCategory && (
          <>
            {product.subCategoryId ? (
              <Link to={`/subcategory/${product.subCategoryId}`} className="text-gray-400 hover:text-primary-600 transition-colors">
                {product.subCategory}
              </Link>
            ) : (
              <span className="text-gray-400">{product.subCategory}</span>
            )}
            <span>/</span>
          </>
        )}
        <span className="text-gray-900 font-medium line-clamp-1">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 xl:gap-12">

        {/* ── Image Gallery ── */}
        <div className="flex flex-col gap-3">
          {/* Main Image */}
          <div
            className="relative rounded-2xl overflow-hidden bg-gray-100 shadow-sm group select-none"
            style={{ aspectRatio: '4/5' }}
            onTouchStart={onImageTouchStart}
            onTouchEnd={onImageTouchEnd}
          >
            {selectedImage ? (
              <img
                src={selectedImage}
                alt={product.name}
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => setLightboxOpen(true)}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-300">
                <Package className="w-16 h-16 mb-2" />
                <span className="text-sm">No image</span>
              </div>
            )}

            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col gap-2">
              {product.isFeatured && (
                <span className="flex items-center gap-1 bg-amber-400 text-amber-900 text-xs font-semibold px-2.5 py-1 rounded-full shadow">
                  <Sparkles className="w-3 h-3" />
                  Featured
                </span>
              )}
              {discount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow">
                  -{discount}% OFF
                </span>
              )}
            </div>

            {!product.inStock && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="bg-white text-gray-900 font-semibold px-4 py-2 rounded-full">
                  Out of Stock
                </span>
              </div>
            )}

            {/* Prev / Next */}
            {displayImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); goPrev(); }}
                  aria-label="Previous image"
                  className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-full bg-white/80 text-gray-700 shadow hover:bg-white opacity-80 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); goNext(); }}
                  aria-label="Next image"
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-full bg-white/80 text-gray-700 shadow hover:bg-white opacity-80 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Zoom hint */}
            {selectedImage && (
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
                aria-label="Zoom image"
                className="absolute bottom-3 right-3 flex items-center justify-center w-9 h-9 rounded-full bg-white/80 text-gray-700 shadow hover:bg-white transition-colors"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Thumbnails */}
          {displayImages.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {displayImages.map((img, idx) => (
                <button
                  key={img.id ?? idx}
                  onClick={() => setSelectedIndex(idx)}
                  className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 transition-all ${
                    selectedIndex === idx
                      ? 'border-primary-600 shadow-md'
                      : 'border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <img src={img.url} alt={img.altText || `View ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Product Info ── */}
        <div className="flex flex-col">

          {/* Category & Name */}
          {product.category && (
            <span className="text-xs font-semibold text-primary-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
              <Tag className="w-3 h-3" />
              {product.category}{product.subCategory ? ` · ${product.subCategory}` : ''}
            </span>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight mb-3">
            {product.name}
          </h1>

          {/* Rating */}
          {product.rating > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${star <= Math.round(product.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`}
                  />
                ))}
              </div>
              <span className="text-sm font-medium text-gray-700">{product.rating.toFixed(1)}</span>
              {product.reviews > 0 && (
                <span className="text-sm text-gray-500">({product.reviews} reviews)</span>
              )}
            </div>
          )}

          {/* Price */}
          <div className="flex items-baseline gap-3 mb-5">
            <span className="text-3xl font-bold text-gray-900">
              ৳{Number(displayPrice).toLocaleString()}
            </span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-lg text-gray-400 line-through">
                ৳{Number(product.originalPrice).toLocaleString()}
              </span>
            )}
            {discount > 0 && (
              <span className="text-sm font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">
                Save {discount}%
              </span>
            )}
          </div>

          {/* Size Selector */}
          {product.variants && product.variants.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2.5">
                <h3 className="text-sm font-semibold text-gray-900">
                  Size
                  {selectedVariant && (
                    <span className="ml-2 font-normal text-gray-500">– {selectedVariant.size}</span>
                  )}
                </h3>
                {selectedVariant && selectedVariant.stockQuantity > 0 && selectedVariant.stockQuantity <= 3 && (
                  <span className="text-xs text-red-600 font-semibold">
                    Only {selectedVariant.stockQuantity} left
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map(variant => {
                  const outOfStock = variant.stockQuantity === 0;
                  const isSelected = selectedVariant?.id === variant.id;
                  return (
                    <button
                      key={variant.id}
                      onClick={() => !outOfStock && setSelectedVariant(variant)}
                      disabled={outOfStock}
                      className={`relative px-4 py-2 text-sm font-medium rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : outOfStock
                          ? 'border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed'
                          : 'border-gray-200 text-gray-700 hover:border-gray-400 cursor-pointer'
                      }`}
                    >
                      {variant.size}
                      {outOfStock && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="w-full h-px bg-gray-300 rotate-12 absolute" />
                        </span>
                      )}
                      {!outOfStock && variant.stockQuantity <= 3 && (
                        <span className="absolute -top-1.5 -right-1.5 bg-amber-400 text-amber-900 text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                          {variant.stockQuantity}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {product.variants.some(v => v.stockQuantity === 0) && (
                <p className="text-xs text-gray-400 mt-2">Crossed sizes are currently unavailable.</p>
              )}
            </div>
          )}

          {/* Quantity */}
          <div className="mb-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-2.5">Quantity</h3>
            <div className="flex items-center gap-3">
              <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center text-sm font-semibold text-gray-900 border-x border-gray-200">
                  {quantity}
                </span>
                <button
                  onClick={handleIncreaseQuantity}
                  className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Add to Cart */}
          <div className="flex gap-3 mb-6">
            <button
              onClick={handleAddToCart}
              disabled={!canAdd}
              className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl font-semibold text-sm transition-all ${
                canAdd
                  ? addedFeedback
                    ? 'bg-emerald-500 text-white'
                    : 'bg-primary-600 text-white hover:bg-primary-700 active:scale-95'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <ShoppingCart className="w-5 h-5" />
              {!canAdd
                ? 'Out of Stock'
                : addedFeedback
                ? 'Added to Cart!'
                : 'Add to Cart'}
            </button>
          </div>

          {/* Product Details */}
          <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden px-4">
            {product.material && (
              <InfoSection title="Material & Fabric" defaultOpen={true}>
                {product.material}
              </InfoSection>
            )}
            {product.careInstructions && (
              <InfoSection title="Care Instructions">
                {product.careInstructions}
              </InfoSection>
            )}
            {(product.sku || product.description) && (
              <InfoSection title="Product Details" defaultOpen={true}>
                <div className="space-y-3">
                  {product.description && (
                    <div>
                      <span className="text-gray-400 block mb-1">Description</span>
                      <p className="whitespace-pre-line text-gray-700">{product.description}</p>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {product.sku && (
                      <div className="flex gap-2">
                        <span className="text-gray-400 w-20">SKU</span>
                        <span>{product.sku}</span>
                      </div>
                    )}
                    {product.category && (
                      <div className="flex gap-2">
                        <span className="text-gray-400 w-20">Category</span>
                        <span>{product.category}</span>
                      </div>
                    )}
                    {product.subCategory && (
                      <div className="flex gap-2">
                        <span className="text-gray-400 w-20">Sub-Category</span>
                        <span>{product.subCategory}</span>
                      </div>
                    )}
                  </div>
                </div>
              </InfoSection>
            )}
          </div>

          {/* Back link */}
          <div className="mt-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Collection
            </Link>
          </div>
        </div>
      </div>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={selectedIndex}
        slides={displayImages.map(img => ({ src: img.url, alt: img.altText || product.name }))}
        on={{ view: ({ index }) => setSelectedIndex(index) }}
        plugins={[Zoom, Thumbnails]}
        zoom={{ maxZoomPixelRatio: 4, scrollToZoom: true }}
        thumbnails={{ position: 'bottom', border: 0 }}
        carousel={{ finite: false }}
        styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.92)' } }}
      />
    </div>
  );
};

export default ProductPage;
